import { useTranslation } from 'react-i18next';
// pages/settings/Organization.tsx
import { useEffect, useRef, useState } from "react";
import { Card } from "react-bootstrap";
import debounce from "debounce-promise";
import { useToast } from "../../context/Toast";
import { getCompanyId, updateCompany } from "../../services/company/company";
import { getAdminsByCompany } from "../../services/company/company";
import { useAuth } from "../../context/AuthContext";
import {
    Title,
    Subtitle,
    InputConfig,
    SaveButton,
    Arrow,
    CardManagerConfig,
    CardProfilePic,
    ContainerConfigGeneral,
    StyledCol,
    StyledColDetails,
    StyledColProfile,
    ContainerButton,
    ContainerConfigOrganization,
} from "./styles";
import { Box, Button, Chip, FormControl, InputLabel, MenuItem, OutlinedInput, Select, SelectChangeEvent, selectClasses, TextField, useTheme } from "@mui/material";
import { User } from "../../interfaces/user.interface";
import Autocomplete from "./Autocomplete";
import { patchUser, searchUsers } from "../../services/user/user";
import { UserRole } from "../../enums/user";
import { browserSession } from "../../services/utils/browserSession";
import { getApiErrorMessage } from "../../services/utils/apiError";

const timeZones = [
    { label: "(GMT-03:00) São Paulo", value: "-03:00" },
    { label: "(GMT-05:00) Horário Padrão do Leste (EUA e Canadá)", value: "-05:00" },
    { label: "(GMT+01:00) Berlim, Paris, Madri", value: "+01:00" },
    { label: "(GMT+09:00) Tóquio, Seul", value: "+09:00" },
    { label: "(GMT+05:30) Índia (Nova Delhi, Mumbai)", value: "+05:30" },
    { label: "(GMT 00:00) Horário de Greenwich (Londres, Lisboa)", value: "00:00" },
];

type ErrorHandlerOptions = {
    setLoading?: (loading: boolean) => void;
    redirectTo?: string;
    context?: 'admin' | 'user' | string;
};




export default function Organization() {
  const { t } = useTranslation('settings');
    const fileInputRef: any = useRef(null);
    const [loading, setLoading] = useState(false);
    const [controlLoading, setControlLoading] = useState(false);
    const { showToast } = useToast();
    const { user, SignOut } = useAuth();
    const informedRequesterId = browserSession.getIdentity()?.user?.id ?? user?.user?.id;
    const [companyData, setCompanyData] = useState({
        id: "",
        name_company: "",
        cnpj: "",
        photo_logo: "",
        number_license_acquired: 1000,
        admin_id: "",
        utc_offset: "",
        surveys_all_month: false,
    });

    const handleApiError = (error: any, options: ErrorHandlerOptions = {}): void => {
        const { setLoading, redirectTo = '/admin/signup', context = 'admin' } = options;

        console.error(error);

        if (error?.response?.status === 401 || error?.response?.data?.error === 'Erro ao validar o token - zenbox_backend') {
            showToast(t('organization.sessionExpired'), 'info', 7500);
            setTimeout(() => {
                showToast(t('organization.redirecting'), 'info', 5000);
                setTimeout(() => {
                    SignOut(redirectTo);
                }, 5000);
            }, 2500);

        } else if (error?.response?.status === 403) {
            showToast(t('organization.permissionDenied'), 'error', 7500);

            setTimeout(() => {
                showToast(t('organization.redirecting'), 'info', 5000);
                setTimeout(() => {
                    SignOut(redirectTo);
                }, 5000);
            }, 2500);

        } else {
            showToast(
                getApiErrorMessage(
                    error,
                    'Ops! Algo não correu bem :(\n\nNosso time de engenheiros foi notificado e iremos corrigir essa questão o quanto antes. Para falar com nosso suporte, utilize o botão "Ajuda" na parte inferior da tela.'
                ),
                'error',
                15000
            );

            if (setLoading)
                setLoading(false);
            throw error;
        }
    }

    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                const company = user.company_visited?.id;
                if (!company) {
                    showToast(t('organization.sessionExpired'), 'info', 7500);
                    setTimeout(() => {
                        showToast(t('organization.redirecting'), 'info', 5000);
                        setTimeout(() => {
                            SignOut("/admin/signup");
                        }, 5000);
                    }, 2500);
                    return;
                }
                const dataForm = await getCompanyId(company);
                let newForm = { ...companyData };
                if (dataForm.Item) {
                    newForm.id = dataForm.Item.id;
                    newForm.name_company = dataForm.Item.name_company;
                    newForm.cnpj = dataForm.Item.cnpj;
                    newForm.photo_logo = dataForm.Item.photo_logo;
                    newForm.number_license_acquired = dataForm.Item.number_license_acquired;
                    newForm.admin_id = dataForm.Item.admin_id ? dataForm.Item.admin_id : "";
                    newForm.utc_offset = dataForm.Item.utc_offset;
                    newForm.surveys_all_month = dataForm.Item.surveys_all_month;
                }
                setCompanyData(newForm);

                const companyAdmins: User[] = await getAdminsByCompany(company);
                const companyActiveUsers: User[] = (await searchUsers({ company_id: company })).data;
                setCompanyActiveUsers(companyActiveUsers);
                setAdminsSelected(companyAdmins.map(admin => ({ id: admin.id, label: admin.first_name + ' ' + admin.last_name })));

            } catch (e: any) {
                handleApiError(e);
            }
            setLoading(false);
        };
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const onSubmitCompany = async () => {
        try {
            setControlLoading(true);
            await updateCompany({ ...companyData, informedRequesterId });
            setControlLoading(false);
            showToast(t('organization.updated'), "success");
        } catch (error) {
            setControlLoading(false);
            handleApiError(error);
        }
    };

    const handleImageChange = (e: any) => {
        const file = e.target.files[0];
        if (file && (file.type === "image/png" || file.type === "image/jpeg")) {
            const reader: any = new FileReader();
            reader.onload = (e: any) => {
                const img = document.createElement("img");
                img.src = e.target.result;
                img.onload = () => {
                    const canvas = document.createElement("canvas");
                    const ctx: any = canvas.getContext("2d");
                    const max_width = 800;
                    const scaleFactor = max_width / img.width;
                    canvas.width = max_width;
                    canvas.height = img.height * scaleFactor;
                    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                    const compressedBase64 = canvas.toDataURL("image/jpeg", 0.6);
                    setCompanyData({ ...companyData, photo_logo: compressedBase64 });
                };
            };
            reader.readAsDataURL(file);
        }
    };


    const handleSelectsChanges = (name: string, value: any): void => {
        setCompanyData({
            ...companyData,
            [name]: value,
        });
    };






    // -- Campo de Administradores --
    const handleAdminChange = async (updatedAdminList: any[]) => {
        // Encontra o admin que está em newAdmins mas não em adminsSelected
        const addedAdmin: { id: string, label: string } = updatedAdminList.find(
            (newAdmin: { id: string, label: string }) => !adminsSelected.some((admin) => admin.id === newAdmin.id)
        );

        if (addedAdmin) {
            try {
                let updatedAdminUser = await patchUser({
                    id: addedAdmin.id,
                    role: UserRole.admin,
                    informedRequesterId,
                });

                if (updatedAdminUser) {
                    showToast(`"${addedAdmin.label}" agora é um administrador!`, 'success', 3500)
                    setAdminsSelected(updatedAdminList);
                }
            } catch (error: any) {
                handleApiError(error);
            }
        }
    };

    const [companyActiveUsers, setCompanyActiveUsers] = useState<User[]>([]);
    const [adminsSelected, setAdminsSelected] = useState<{ id: string, label: string }[]>([]);

    // Função para remover o administrador
    const handleAdminRemove = async (removedAdminId: string) => {
        try {
            // Busque o usuário completo para acessar responsible_groups
            const userToUpdate = companyActiveUsers.find(u => u.id === removedAdminId);

            let newRole = UserRole.employee;
            let toastMsg = `"${adminsSelected.find(admin => admin.id == removedAdminId)?.label}" não é mais um administrador e agora é um colaborador!`;

            if (userToUpdate && Array.isArray(userToUpdate.responsible_groups) && userToUpdate.responsible_groups.length > 0) {
                newRole = UserRole.group_responsible;
                toastMsg = `"${adminsSelected.find(admin => admin.id == removedAdminId)?.label}" não é mais um administrador, mas continua responsável por grupo(s)!`;
            }

            let demotedUser = await patchUser({
                id: removedAdminId,
                role: newRole,
                informedRequesterId,
            });

            if (demotedUser) {
                showToast(toastMsg, 'success', 3500);
                setAdminsSelected((prevAdmins) =>
                    prevAdmins.filter((admin) => admin.id !== removedAdminId)
                );
            }
        } catch (error: any) {
            handleApiError(error);
        }
    };

    // -- --

    return (
        <ContainerConfigOrganization>
            {/* <h3 style={{fontWeight: '600'}}>Detalhes da Organização</h3> */}


            <ContainerConfigGeneral>
                <StyledCol>
                    <CardManagerConfig style={{ background: "#FFF", color: "#3C4043" }}>
                        <Card.Body style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '2px' }}>
                            <div>
                                <Title className="m-0">{t('organization.detailsTitle')}</Title>
                                <Subtitle className="m-0">{t('organization.description')}</Subtitle>
                            </div>
                            <Arrow size={20} />
                        </Card.Body>
                    </CardManagerConfig>
                </StyledCol>

                <StyledColDetails>
                    <CardManagerConfig style={{ background: "#FFF", color: "#3C4043", marginTop: "1rem" }}>
                        <Card.Body className="p-2">
                            <Title>{t('organization.detailsSubtitle')}</Title>

                            <div className="mt-4">
                                <InputLabel id="name_company">{t('organization.name')}</InputLabel>
                                <TextField
                                    id="name_company"
                                    variant="outlined"
                                    disabled
                                    value={companyData?.name_company}
                                    onChange={(event: any) => {
                                        handleSelectsChanges('name_company', event.target.value)
                                    }}
                                    sx={{
                                        width: '100%',
                                        borderRadius: '0.625rem',
                                        backgroundColor: '#F1F1F1',
                                        '& .MuiOutlinedInput-root': {
                                          borderRadius: '0.625rem',
                                          '& input': {
                                            backgroundColor: 'transparent',
                                            fontSize: '16px',
                                          },
                                        },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                          borderColor: '#c9c9c9',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                          borderColor: '#c9c9c9',
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                          borderColor: '#c9c9c9',
                                        },
                                      }}

                                />

                                {/* <InputConfig
                                        style={{ padding: "4px", borderRadius: "6px" }}
                                        id="name_company"
                                        type="text"
                                        value={companyData?.name_company}
                                        onChange={(e) =>
                                            handleSelectsChanges("name_company", e.target.value)
                                        }
                                /> */}

                            </div>

                            <div className="mt-4">
                                <InputLabel id="admin-id">{t('organization.administrators')}</InputLabel>
                                <Autocomplete
                                    id="admins-autocomplete"
                                    options={companyActiveUsers.map(admin => ({ id: admin.id, label: admin.first_name + ' ' + admin.last_name }))}
                                    selectedOptions={adminsSelected}
                                    onChange={handleAdminChange}
                                    onRemove={handleAdminRemove}
                                    label=""
                                    placeholder={String(t('organization.selectPlaceholder'))}
                                />

                                {/* INPUT ANTIGO DESABILITADO */}
                                {/* <StyledAsyncSelectIndex
                                isMulti
                                value={companyData?.admin_id}
                                placeholder=""
                                name="admin_id"
                                defaultOptions={true}
                                loadOptions={debounceOptionsAdmins}
                                noOptionsMessage={() => "Sem opção"}
                                onChange={(value: any) =>
                                handleSelectsChanges("admin_id", value)
                                }
                            /> */}

                            </div>

                            {/* <div className="mt-4">
                                <InputLabel id="utc_offset-label">Fuso Horário</InputLabel>
                                <Select
                                    sx={{
                                        width: '100%',
                                        backgroundColor: '#F1F1F1',
                                        borderRadius: ' 0.625rem',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#c9c9c9',
                                            borderRadius: ' 0.625rem',

                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#c9c9c9',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#c9c9c9',
                                        },
                                        '&.Mui-focused:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#c9c9c9',
                                        },
                                    }}
                                    labelId="utc_offset-label"
                                    id="utc_offset"
                                    value={companyData?.utc_offset || ""}
                                    onChange={(event: any) => {
                                        handleSelectsChanges('utc_offset', event.target.value)
                                    }}

                                >
                                    <MenuItem value="">
                                        <em>Selecione</em>
                                    </MenuItem>
                                    {timeZones.map((timezone, index) => (
                                        <MenuItem key={index} value={timezone.value}>
                                            {timezone.label}
                                        </MenuItem>
                                    ))}
                                </Select>

                            </div> */}


                            {/* <ContainerButton>
                                <Button
                                    className="mt-4"
                                    variant="contained"
                                    disabled={controlLoading}
                                    onClick={onSubmitCompany}
                                    sx={{
                                        textTransform: 'capitalize',
                                        background: 'linear-gradient(116deg, #04A5FF 16.33%, #2B81FF 83.67%)',
                                        borderRadius: '2em',
                                        fontSize: '1em',
                                        fontWeight: 'semibold ',
                                        padding: '0.5em 1.5em',


                                        '@media (max-width:694px)': {
                                            width: '100%',
                                        },
                                    }}
                                >
                                    Salvar
                                </Button>
                            </ContainerButton> */}


                            {/* <Subtitle className="mt-4 mb-1">Fuso Horário</Subtitle>
                            <InputConfig
                                style={{ padding: "4px", borderRadius: "6px" }}
                                id="utc_offset"
                                type="select"
                                placeholder="(GMT-03:00) São Paulo"
                                value={companyData?.utc_offset || ""}
                                onChange={(e) =>
                                    handleSelectsChanges("utc_offset", e.target.value)
                                }
                            >
                                    <option value="">{t('organization.selectPlaceholder')}</option>
                                {timeZones.map((timezone, index) => (
                                    <option key={index} value={timezone.value}>
                                        {timezone.label}
                                    </option>
                                ))}
                            </InputConfig> */}
                            {/* <div className="mt-4">
                                <SaveButton
                                    disabled={controlLoading}
                                    onClick={onSubmitCompany}
                                    style={{ padding: "10px 25px" }}
                                >
                                    Salvar
                                </SaveButton>
                            </div> */}
                        </Card.Body>
                    </CardManagerConfig>

                </StyledColDetails>
                
                
                <StyledColProfile>

                    <CardProfilePic style={{ color: '#ffff' }}>
                    <Card.Body className="m-2">
                        <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexDirection: 'column',
                        width: '100%',
                        }}>

                        <div style={{
                            width: '150px',
                            height: '150px',
                            backgroundColor: 'white',
                            borderRadius: '50%',
                            marginBottom: '25px',
                            backgroundImage: `url(${companyData.photo_logo})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            border: '2px solid #D9D9D9'
                        }}>

                            <input
                            type="file"
                            style={{ display: 'none' }}
                            onChange={handleImageChange}
                            accept="image/png, image/jpeg"
                            ref={fileInputRef}
                            />

                        </div>

                        {/* <Button variant="contained"
                            sx={{
                            textTransform:'capitalize',
                            background:'linear-gradient(116deg, #04A5FF 16.33%, #2B81FF 83.67%)',
                            borderRadius: '2em',
                            fontSize: '1em',
                            fontWeight: 'semibold ',
                            padding: '0.5em 1.5em',


                            '@media (max-width:694px)': {
                                width: '100%',
                            },
                            }}
                        disabled={controlLoading} 
                        onClick={() => fileInputRef.current && fileInputRef.current.click()} 
                        style={{}}
                        >
                            Alterar
                        </Button> */}
                        </div>
                    </Card.Body>

                    </CardProfilePic>

                </StyledColProfile>

               
               
            </ContainerConfigGeneral>
        </ContainerConfigOrganization>
    );
}
